// <animated-lines> — keeps the seamless conveyor lines seam-free on any viewport width.
//
// The section renders two identical motif "groups" per line inside an overflow-hidden belt/track.
// CSS translates the track by exactly ONE group width (--al-shift) per cycle, so the loop is
// seamless and its speed is independent of the group count. But if a single pair of groups is
// narrower than the line (wide / full-width / ultra-wide screens), a one-group translate would
// briefly expose the track's end — a gap at the seam. This element measures each line and clones
// the base group until (groups - 1) cover the line, keeping every group identical so the
// fixed-width translate stays seamless. Direction, timing and reduced-motion are all pure CSS;
// this is layout-only work. Dashed / dotted styles have no track and are ignored.

class AnimatedLines extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;

    this.tracks = Array.from(this.querySelectorAll('[data-al-track]'));
    if (!this.tracks.length) return;

    // Snapshot each track's pristine first group so clones never compound.
    this._bases = this.tracks
      .map((track) => {
        const base = track.querySelector('[data-al-group]');
        if (!base) return null;
        const gw = parseFloat(track.dataset.alGroupw) || base.getBoundingClientRect().width;
        return { track, base, gw };
      })
      .filter(Boolean);

    this._schedule = this._schedule.bind(this);
    this._schedule();

    if ('ResizeObserver' in window) {
      this._ro = new ResizeObserver(this._schedule);
      this._ro.observe(this);
      // When lines break out to full-bleed (100vw) the host box stays capped at the
      // page-width central column, so observing only the box misses ultra-wide resizes
      // beyond that cap. Observe each line too — it tracks the viewport — so the clone
      // reservoir is always refilled to cover the full 100vw and the seam never shows.
      this.tracks.forEach((track) => {
        const line = track.closest('.al-line');
        if (line) this._ro.observe(line);
      });
    }
    window.addEventListener('resize', this._schedule, { passive: true });
  }

  disconnectedCallback() {
    if (this._ro) {
      this._ro.disconnect();
      this._ro = null;
    }
    window.removeEventListener('resize', this._schedule);
    if (this._raf) cancelAnimationFrame(this._raf);
  }

  _schedule() {
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = requestAnimationFrame(() => this._fit());
  }

  _fit() {
    this._bases.forEach(({ track, base, gw }) => {
      if (!gw) return;
      const line = track.closest('.al-line') || track.parentElement;
      const cw = line ? line.clientWidth : 0;
      if (!cw) return;

      // Need (G - 1) groups to cover the line while translating one group → G = ceil(cw / gw) + 1.
      const needed = Math.max(2, Math.ceil(cw / gw) + 1);
      let have = track.querySelectorAll('[data-al-group]').length;
      while (have < needed) {
        track.appendChild(base.cloneNode(true));
        have += 1;
      }
    });
  }
}

if (!customElements.get('animated-lines')) {
  customElements.define('animated-lines', AnimatedLines);
}
